import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { functions as configuredFunctions } from '../config/firebase';
import { addShareAudienceMember, SHARE_VISIBILITY } from './calendarShareService';

function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

function displayNameFromUser(userDoc) {
  if (!userDoc) return '';
  const first = String(userDoc.firstName || '').trim();
  const last = String(userDoc.lastName || '').trim();
  const full = `${first} ${last}`.trim();
  return full || String(userDoc.username || userDoc.email || '').trim();
}

async function findUserByEmail(db, email) {
  const emailLower = normalizeEmail(email);
  if (!emailLower) return null;
  const snap = await getDocs(query(collection(db, 'users'), where('email', '==', emailLower)));
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

async function findOrCreateDirectConversation(db, userA, userB, meta = {}) {
  const participants = [userA, userB].sort();
  const snap = await getDocs(
    query(collection(db, 'conversations'), where('participants', 'array-contains', userA), where('type', '==', 'direct'))
  );
  const existing = snap.docs.find((d) => {
    const p = d.data()?.participants || [];
    return p.length === 2 && p.includes(userB);
  });
  if (existing) return existing.id;

  const ref = await addDoc(collection(db, 'conversations'), {
    participants,
    type: 'direct',
    name: null,
    lastMessage: null,
    lastActivity: serverTimestamp(),
    createdAt: serverTimestamp(),
    createdBy: userA,
    unreadCounts: { [userA]: 0, [userB]: 0 },
    isActive: true,
    ...meta,
  });
  return ref.id;
}

export async function searchUsersForShare(db, term, { excludeUserId = '', excludeEmails = [] } = {}) {
  const trimmed = String(term || '').trim();
  if (trimmed.length < 2) return [];

  const snap = await getDocs(query(collection(db, 'users'), limit(150)));
  const lower = trimmed.toLowerCase();
  const excludeSet = new Set((excludeEmails || []).map(normalizeEmail).filter(Boolean));

  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((u) => u.id !== excludeUserId)
    .filter((u) => normalizeEmail(u.email))
    .filter((u) => !excludeSet.has(normalizeEmail(u.email)))
    .filter((u) => {
      const hay = [
        u.firstName,
        u.lastName,
        u.username,
        u.email,
        `${u.firstName || ''} ${u.lastName || ''}`,
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(lower);
    })
    .slice(0, 6);
}

async function sendCalendarShareEmail(recipient, emailData) {
  const handleEmail = httpsCallable(configuredFunctions || getFunctions(), 'handleEmail');
  await handleEmail({
    template: 'calendar_share_link',
    recipient: normalizeEmail(recipient),
    emailData,
  });
}

async function notifyCalendarShareInApp(db, {
  ownerId,
  ownerName,
  recipientUserId,
  shareUrl,
  shareToken,
  linkId,
  tourName,
  dateKey,
  dateLabel,
  message,
}) {
  const conversationId = await findOrCreateDirectConversation(db, ownerId, recipientUserId);
  const preview = `Shared day · ${tourName || 'Tour'}`;
  const text =
    String(message || '').trim() ||
    `${ownerName || 'Someone'} shared show day details with you. Tap below to view.`;

  await addDoc(collection(db, 'conversations', conversationId, 'messages'), {
    text,
    senderId: ownerId,
    senderName: ownerName || 'Tour owner',
    senderPhoto: '',
    timestamp: serverTimestamp(),
    type: 'calendar_share',
    shareUrl: String(shareUrl || '').trim(),
    shareToken: String(shareToken || '').trim(),
    linkId: String(linkId || '').trim(),
    tourName: String(tourName || 'Tour').trim(),
    dateKey: String(dateKey || '').trim(),
    dateLabel: String(dateLabel || dateKey || '').trim(),
    readBy: { [ownerId]: serverTimestamp() },
    edited: false,
  });

  const convSnap = await getDoc(doc(db, 'conversations', conversationId));
  const prevUnread = convSnap.data()?.unreadCounts?.[recipientUserId] || 0;
  await updateDoc(doc(db, 'conversations', conversationId), {
    lastMessage: {
      text: preview,
      senderId: ownerId,
      timestamp: serverTimestamp(),
      type: 'calendar_share',
    },
    lastActivity: serverTimestamp(),
    [`unreadCounts.${recipientUserId}`]: prevUnread + 1,
  });
}

export async function recordShareLinkEmailSent(db, ownerId, linkId, entries) {
  if (!entries?.length) return;
  const ref = doc(db, 'users', ownerId, 'calendarShareLinks', linkId);
  await setDoc(
    ref,
    {
      emailLog: arrayUnion(...entries),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export async function sendShareLinkToRecipients({
  db,
  ownerId,
  ownerName,
  linkId,
  shareToken,
  shareUrl,
  tourName,
  dateKey,
  dateLabel,
  visibility,
  recipients,
  message = '',
}) {
  const list = Array.isArray(recipients) ? recipients : [];
  if (!list.length) throw new Error('Add at least one recipient.');

  const failures = [];
  const successes = [];
  const note = String(message || '').trim();

  for (const person of list) {
    const email = normalizeEmail(person.email);
    if (!email) continue;

    let userId = String(person.userId || '').trim();
    let name = String(person.name || '').trim();
    let phone = String(person.phone || '').trim();

    if (!userId) {
      const user = await findUserByEmail(db, email);
      if (user) {
        userId = user.id;
        if (!name) name = displayNameFromUser(user);
        if (!phone) phone = String(user.phone || user.phoneNumber || '').trim();
      }
    }

    if (visibility === SHARE_VISIBILITY.ACCOUNT) {
      try {
        await addShareAudienceMember(db, ownerId, linkId, {
          email,
          userId,
          name,
          phone,
        });
      } catch (err) {
        console.warn('addShareAudienceMember', err);
      }
    }

    try {
      await sendCalendarShareEmail(email, {
        recipientName: name || email.split('@')[0],
        senderName: ownerName || 'Tour team',
        tourName: tourName || 'Tour',
        dateLabel: dateLabel || dateKey || 'Show day',
        shareLink: shareUrl,
        message: note,
      });
    } catch (err) {
      console.warn('calendar share email failed', email, err);
      failures.push(email);
      continue;
    }

    if (userId && userId !== ownerId) {
      try {
        await notifyCalendarShareInApp(db, {
          ownerId,
          ownerName,
          recipientUserId: userId,
          shareUrl,
          shareToken,
          linkId,
          tourName,
          dateKey,
          dateLabel,
          message: note,
        });
      } catch (err) {
        console.warn('calendar share in-app notify failed', err);
      }
    }

    successes.push({
      email,
      userId,
      name,
      sentAt: new Date().toISOString(),
      sentBy: ownerId,
    });
  }

  if (successes.length) {
    await recordShareLinkEmailSent(db, ownerId, linkId, successes);
  }

  if (failures.length === list.length) {
    throw new Error('Could not send share emails. Check your connection and try again.');
  }
  if (failures.length) {
    throw new Error(
      `Sent to ${successes.length} of ${list.length} recipients. Failed: ${failures.join(', ')}`
    );
  }
}
